import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { formatDate, formatMoney } from '@/utils/format';

export interface SeatAddPreview {
  quantity: number;
  unitAmountMinor: number;
  monthlyPerSeatMinor: number;
  billingInterval: 'monthly' | 'annual';
  prorationAmountMinor: number;
  currency: string;
  periodEnd: string;
  purchasedSeatsAfter: number;
  totalSeatsAfter: number;
}

export const SeatUpgradeDialog = ({
  open,
  onOpenChange,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded?: () => void;
}) => {
  const { t } = useTranslation('billing');
  const { t: tCommon } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const previewQuery = useQuery({
    queryKey: ['billing-seat-add-preview'],
    queryFn: () => api.get<SeatAddPreview>('/billing/seats/preview-add'),
    enabled: open,
  });

  const addSeat = useMutation({
    mutationFn: () => api.post<SeatAddPreview>('/billing/seats/add', {}),
    onSuccess: async () => {
      toast.success(t('seats.addSuccess'));
      onOpenChange(false);
      await queryClient.invalidateQueries({ queryKey: ['billing-summary'] });
      await queryClient.invalidateQueries({ queryKey: ['billing-seat-add-preview'] });
      onAdded?.();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const preview = previewQuery.data;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-visible" closeLabel={tCommon('action.close')}>
        <DialogHeader>
          <DialogTitle>{t('seats.addTitle')}</DialogTitle>
        </DialogHeader>

        <DialogBody contentClassName="space-y-4">
          {previewQuery.isLoading ? (
            <p className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>
          ) : previewQuery.error ? (
            <p className="text-body-sm text-danger">
              {previewQuery.error instanceof ApiError
                ? tErrors(previewQuery.error.code, previewQuery.error.params)
                : tErrors('UNEXPECTED')}
            </p>
          ) : preview ? (
            <>
              <p className="text-body-sm text-fg-secondary">{t('seats.addDescription')}</p>
              <dl className="space-y-2 text-body-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-fg-secondary">{t('seats.addMonthlyRate')}</dt>
                  <dd className="font-medium text-fg">
                    {formatMoney(preview.monthlyPerSeatMinor, preview.currency)}
                    {t('checkout.perMonth')}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-fg-secondary">{t('seats.addProration')}</dt>
                  <dd className="font-medium text-fg">
                    {formatMoney(preview.prorationAmountMinor, preview.currency)}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-fg-secondary">{t('seats.addDueOn')}</dt>
                  <dd className="font-medium text-fg">{formatDate(preview.periodEnd)}</dd>
                </div>
              </dl>
              <p className="text-caption text-fg-secondary">
                {t('seats.addProrationNote', {
                  date: formatDate(preview.periodEnd),
                })}
              </p>
              <p className="text-caption text-fg-secondary">
                {t('seats.addTotalAfter', { count: preview.totalSeatsAfter })}
              </p>
            </>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {tCommon('action.cancel')}
          </Button>
          <Button
            loading={addSeat.isPending}
            disabled={!preview || previewQuery.isLoading || Boolean(previewQuery.error)}
            onClick={() => addSeat.mutate()}
          >
            {t('seats.addConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
